import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, X, BookOpen } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000; // 2 seconds

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/payment/status/${sessionId}`);
          const data = response.data;

          if (data.payment_status === 'paid') {
            setStatus('success');
            setPaymentData(data);
            return;
          } else if (data.status === 'expired') {
            setStatus('expired');
            return;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setStatus('timeout');
          }
        } catch (error) {
          console.error('Error checking payment:', error);
          setStatus('error');
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8">
          {status === 'checking' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-orange-600 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
              <p className="text-gray-600">Please wait while we confirm your purchase.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for your purchase! Your eBook has been sent to your email.
              </p>
              {paymentData && (
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-sm text-gray-600">Amount Paid:</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${paymentData.amount?.toFixed(2)}
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <a
                  href="https://customer-assets.emergentagent.com/job_less-pay-secrets/artifacts/1rgke7ht_The%20Dealership%20Killer%20E%20Book.pdf"
                  download="The-Dealership-Killer-eBook.pdf"
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-lg inline-flex items-center justify-center"
                >
                  <BookOpen className="mr-2 w-5 h-5" />
                  Download Your eBook Now
                </a>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full"
                >
                  Back to Home
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                💌 Check your email - we've also sent you a download link!
              </p>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
              </h2>
              <p className="text-gray-600 mb-6">
                {status === 'expired'
                  ? 'Your payment session has expired. Please try again.'
                  : 'We couldn\'t verify your payment. Please contact support if you were charged.'}
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                Back to Home
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;